import {
  useEffect,
  useId,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'
import { cx } from './utils'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export type DialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  closeLabel: string
  children: ReactNode
  triggerRef?: RefObject<HTMLElement | null>
  position?: 'center' | 'right'
  className?: string
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  children,
  triggerRef,
  position = 'center',
  className,
}: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    returnFocusRef.current = triggerRef?.current ?? (document.activeElement as HTMLElement | null)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const panel = panelRef.current
    const firstFocusable = panel?.querySelector<HTMLElement>(focusableSelector)
    window.requestAnimationFrame(() => (firstFocusable ?? panel)?.focus())

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onOpenChange(false)
        return
      }
      if (event.key !== 'Tab' || !panel) return
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector))
      if (focusable.length === 0) {
        event.preventDefault()
        panel.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      window.requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }, [open, onOpenChange, triggerRef])

  if (!open) return null

  const closeFromBackdrop = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!(event.target as HTMLElement).closest('.dialog')) onOpenChange(false)
  }

  return createPortal(
    <div className="dialog-layer" onMouseDown={closeFromBackdrop}>
      <div className="dialog-layer__backdrop" aria-hidden="true" />
      <div
        ref={panelRef}
        className={cx('dialog', `dialog--${position}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <div className="dialog__header">
          <div>
            <h2 className="dialog__title" id={titleId}>{title}</h2>
            {description ? <p className="dialog__description" id={descriptionId}>{description}</p> : null}
          </div>
          <Button
            variant="quiet"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label={closeLabel}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
        <div className="dialog__content">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
